/**
 * Link the signed-in Aelo user to their Uspacy employee (#746, spec
 * `docs/plans/2026-10-29-uspacy-card-tab-session.md` §6, §10.3).
 *
 * The Aelo tab in a Uspacy card cannot say WHO is looking, only that the
 * portal vouches for someone. The employee presses "Link" there, the
 * extension asks the server for a one-time ticket and opens this page with it.
 * Here a signed-in Aelo user — a member of the ticket's organization — confirms
 * that the Uspacy employee is them. Everything shown comes from the server's
 * reading of the ticket, never from the address.
 *
 * The ticket is taken out of the URL fragment at module load, before the router
 * exists (`lib/uspacy-employee-link-ticket.ts`); `?org=` is applied by the
 * shared organization-link mechanism (`lib/org-link.ts`, #1232).
 */
import { useCallback, useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { UspacyLinkPageShell } from '@/components/settings/UspacyLinkPageShell';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { ApiError, apiErrorBody } from '@/lib/fetch-utils';
import {
  forgetEmployeeLinkTicket,
  readEmployeeLinkTicket,
} from '@/lib/uspacy-employee-link-ticket';
import {
  uspacyEmployeeLinkPreviewSchema,
  uspacyEmployeeLinkResultSchema,
  type UspacyEmployeeLinkPreview,
} from '@aica/api-types';

// Bare import on purpose: the capture must run when the route's main chunk
// loads, not when the (code-split) component does.
import '@/lib/uspacy-employee-link-ticket';

export const Route = createFileRoute('/integrations/uspacy/employee-link')({
  component: UspacyEmployeeLinkPage,
});

/** What the page says instead of a form. `forbidden`: owner or not a member. */
type Problem = 'ticketInvalid' | 'linkedToOther' | 'forbidden' | 'generic';

type View =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: UspacyEmployeeLinkPreview; linking: boolean }
  | { kind: 'linked' }
  | { kind: 'problem'; problem: Problem };

const OTHER_USER_REFUSAL = [{ status: 409, code: 'employee_linked_to_other_user' }];

async function classify(error: unknown): Promise<Problem> {
  if (!(error instanceof ApiError)) return 'generic';
  if (error.status === 403) return 'forbidden';
  const code = (await apiErrorBody(error))?.code;
  if (error.status === 404 && code === 'ticket_invalid') return 'ticketInvalid';
  if (error.status === 409 && code === 'employee_linked_to_other_user') {
    return 'linkedToOther';
  }
  return 'generic';
}

function UspacyEmployeeLinkPage() {
  const { t } = useTranslation('settings');
  const { orgId, isLoading: authLoading } = useAuth();
  const { authenticatedFetch } = useAuthFetch();

  const [ticket, setTicket] = useState(readEmployeeLinkTicket);
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const fail = useCallback(async (error: unknown) => {
    const problem = await classify(error);
    // A dead ticket is dead for good; any other refusal leaves it usable.
    if (problem === 'ticketInvalid') forgetEmployeeLinkTicket();
    setView({ kind: 'problem', problem });
  }, []);

  useEffect(() => {
    if (!ticket || !orgId) return;
    let cancelled = false;
    setView({ kind: 'loading' });
    authenticatedFetch<unknown>(
      `/api/v1/organizations/${orgId}/uspacy/employee-link/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ ticket }),
      }
    )
      .then((body) => {
        if (cancelled) return;
        const preview = uspacyEmployeeLinkPreviewSchema.parse(body);
        setView({ kind: 'ready', preview, linking: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) void fail(error);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, authenticatedFetch, fail, orgId, ticket]);

  const link = useCallback(async () => {
    if (!ticket || !orgId || view.kind !== 'ready') return;
    const { preview } = view;
    setView({ kind: 'ready', preview, linking: true });
    try {
      const body = await authenticatedFetch<unknown>(
        `/api/v1/organizations/${orgId}/uspacy/employee-link`,
        {
          method: 'POST',
          body: JSON.stringify({ ticket }),
          expectedRefusals: OTHER_USER_REFUSAL,
        }
      );
      uspacyEmployeeLinkResultSchema.parse(body);
      forgetEmployeeLinkTicket();
      // The preview effect keys on the ticket: dropping it keeps a re-run from
      // asking about a ticket that was just spent.
      setTicket(null);
      setView({ kind: 'linked' });
    } catch (error) {
      await fail(error);
    }
  }, [authenticatedFetch, fail, orgId, ticket, view]);

  if (view.kind === 'linked') {
    return (
      <UspacyLinkPageShell title={t('uspacyEmployeeLink.title')}>
        <p className="text-sm text-fg">{t('uspacyEmployeeLink.done')}</p>
        <p className="text-sm text-fg-muted">{t('uspacyEmployeeLink.doneHint')}</p>
      </UspacyLinkPageShell>
    );
  }

  if (!ticket) {
    return (
      <UspacyLinkPageShell title={t('uspacyEmployeeLink.title')}>
        <p className="text-sm text-fg-muted">{t('uspacyEmployeeLink.noTicket')}</p>
      </UspacyLinkPageShell>
    );
  }

  // Auth has settled and there is no organization to ask (a platform owner, or
  // nothing selected): the preview effect never starts, and without this the
  // page would say «Checking the link…» for good. The same words as the
  // server's 403 for an owner or a non-member.
  const noOrganization = !authLoading && !orgId;
  if (noOrganization || view.kind === 'problem') {
    const problem: Problem =
      !noOrganization && view.kind === 'problem' ? view.problem : 'forbidden';
    return (
      <UspacyLinkPageShell title={t('uspacyEmployeeLink.title')}>
        <p className="text-sm text-danger" role="alert">
          {t(`uspacyEmployeeLink.problem.${problem}`)}
        </p>
        {problem === 'generic' ? (
          <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
            {t('uspacyEmployeeLink.retry')}
          </Button>
        ) : null}
      </UspacyLinkPageShell>
    );
  }

  if (view.kind === 'loading') {
    return (
      <UspacyLinkPageShell title={t('uspacyEmployeeLink.title')}>
        <p className="text-sm text-fg-muted">{t('uspacyEmployeeLink.loading')}</p>
      </UspacyLinkPageShell>
    );
  }

  const { preview, linking } = view;
  return (
    <UspacyLinkPageShell title={t('uspacyEmployeeLink.title')}>
      <p className="text-sm text-fg-muted">{t('uspacyEmployeeLink.intro')}</p>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-fg-muted">{t('uspacyEmployeeLink.portal')}</dt>
        <dd className="text-fg">{preview.portalDomain}</dd>
        <dt className="text-fg-muted">{t('uspacyEmployeeLink.employee')}</dt>
        <dd className="text-fg">
          {preview.employeeName ?? t('uspacyEmployeeLink.employeeUnnamed')}
        </dd>
        <dt className="text-fg-muted">{t('uspacyEmployeeLink.organization')}</dt>
        <dd className="text-fg">{preview.organizationName}</dd>
      </dl>

      <p className="rounded-card bg-warning-dim p-3 text-sm text-warning-fg">
        {t('uspacyEmployeeLink.warning')}
      </p>

      <Button onClick={() => void link()} disabled={linking}>
        {linking ? t('uspacyEmployeeLink.linking') : t('uspacyEmployeeLink.submit')}
      </Button>
    </UspacyLinkPageShell>
  );
}
